import React, { Component } from "react";
import { Modal } from "react-bootstrap";
import {
  fetchOutstandingDocuments,
  acceptLegalDocumentVersion,
  legalDocumentPath,
} from "./legalApi";
import "./ConsentGate.css";

/**
 * Reusable, blocking post-authentication consent gate.
 *
 * After login (password, Google, or any other method) this checks the backend
 * for the user's OUTSTANDING required documents. If any are owed, it shows a
 * modal the user cannot dismiss until every document is accepted. There is no
 * "skip / later". Once accepted, it does not nag again for the session.
 *
 * Enforcement is primarily server-side (the outstanding-requirements API); this
 * component is the UX surface and reuses existing app patterns (react-bootstrap
 * Modal) without redesigning any locked surface.
 */

// Session-scoped memo so the gate does not re-fetch / re-flash on every route.
const clearedForUser = {};

class ConsentGate extends Component {
  state = {
    show: false,
    loading: false,
    submitting: false,
    documents: [],
    accepted: {},
    error: null,
  };

  componentDidMount() {
    this._mounted = true;
    this.checkOutstanding();
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  currentUserId() {
    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("accessToken");
    if (!userId || !token) {
      return null;
    }
    return userId;
  }

  checkOutstanding() {
    const userId = this.currentUserId();
    if (!userId || clearedForUser[userId]) {
      return;
    }

    this.setState({ loading: true });
    fetchOutstandingDocuments()
      .then(({ count, documents }) => {
        if (!this._mounted) {
          return;
        }
        if (count > 0 && documents.length > 0) {
          const accepted = {};
          documents.forEach((doc) => {
            accepted[doc.version_id] = false;
          });
          this.setState({ show: true, documents, accepted, loading: false });
        } else {
          clearedForUser[userId] = true;
          this.setState({ show: false, loading: false });
        }
      })
      .catch(() => {
        if (this._mounted) {
          this.setState({ loading: false });
        }
      });
  }

  toggle = (versionId) => (event) => {
    const checked = event.target.checked;
    this.setState((prev) => ({
      accepted: { ...prev.accepted, [versionId]: checked },
    }));
  };

  allAccepted() {
    return this.state.documents.every((doc) => !!this.state.accepted[doc.version_id]);
  }

  handleContinue = () => {
    if (!this.allAccepted() || this.state.submitting) {
      return;
    }
    this.setState({ submitting: true, error: null });

    const requests = this.state.documents.map((doc) =>
      acceptLegalDocumentVersion(doc.version_id)
    );

    Promise.all(requests)
      .then(() => {
        const userId = this.currentUserId();
        if (userId) {
          clearedForUser[userId] = true;
        }
        if (this._mounted) {
          this.setState({ show: false, submitting: false });
        }
      })
      .catch(() => {
        if (this._mounted) {
          this.setState({
            submitting: false,
            error: "We could not record your acceptance. Please try again.",
          });
        }
      });
  };

  render() {
    const { show, documents, accepted, submitting, error } = this.state;

    if (!show) {
      return null;
    }

    return (
      <Modal
        show={show}
        backdrop="static"
        keyboard={false}
        onHide={() => {}}
        centered
        dialogClassName="legal-consent-gate-modal"
      >
        <Modal.Header>
          <Modal.Title>Please review and accept</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            To continue using Pentecost+, please review and accept the following
            {documents.length > 1 ? " documents" : " document"}:
          </p>
          <ul className="list-unstyled">
            {documents.map((doc) => (
              <li key={doc.version_id}>
                <label className="legal-consent-gate-item">
                  <input
                    type="checkbox"
                    checked={!!accepted[doc.version_id]}
                    onChange={this.toggle(doc.version_id)}
                  />
                  <span>
                    <span className="legal-consent-gate-prefix">
                      I agree to the{" "}
                    </span>
                    <a
                      href={doc.url || legalDocumentPath(doc.slug)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="legal-consent-gate-link"
                    >
                      {doc.title}
                    </a>{" "}
                    <span className="legal-consent-gate-version">
                      (Version {doc.version})
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {error ? <p className="text-danger">{error}</p> : null}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-danger"
            disabled={!this.allAccepted() || submitting}
            onClick={this.handleContinue}
          >
            {submitting ? "Saving..." : "Accept & Continue"}
          </button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default ConsentGate;
